import { z } from 'zod';
import { API_BASE, apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  POCZEKALNIA KOPII — paczka wgrana z komputera, gdy wyspy JESZCZE NIE MA.
 *
 *  ⚠️⚠️ ZAMÓWIONE 08.09.2026: „czy możemy dodać, by backup z komputera można było wrzucać na zasób
 *  backupowy, bez wyspy? żeby był gotowy jak ona powstanie".
 *
 *  ⚠️⚠️ OSOBNE TRASY, BO TU NIE MA `groupId` — a wgranie na wyspę jest adresowane do WĘZŁA
 *  (`agents/:id/kopia-wgraj`). Wyspy nie da się utworzyć z portalu: rodzi się przy rejestracji
 *  pierwszego agenta, z kodu założycielskiego. Przed przyjazdem maszyny istnieje więc tylko kod —
 *  i to on jest punktem, do którego paczkę przypinamy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

const PrzypiecieSchema = z.discriminatedUnion('rodzaj', [
  z.object({ rodzaj: z.literal('WYSPA'), nazwa: z.string().nullable(), id: z.string() }),
  z.object({
    rodzaj: z.literal('KOD'),
    nazwa: z.string(),
    kod: z.string().nullable(),
    id: z.string(),
    /** Wyspa, którą ten kod JUŻ założył — `null`, dopóki nie powstała. */
    wyspaId: z.string().nullable(),
  }),
  z.object({ rodzaj: z.literal('POLKA') }),
]);

export const PaczkaPoczekalniSchema = z.object({
  id: z.string(),
  nazwa: z.string(),
  odOperatora: z.string(),
  rozmiar: z.number().nullable(),
  suma: z.string().nullable(),
  error: z.string().nullable(),
  maPlik: z.boolean(),
  createdAt: z.string(),
  przezKogo: z.string().nullable(),
  klient: z.string(),
  przejeta: z.object({ zadanieId: z.string(), kiedy: z.string().nullable() }).nullable(),
  przypiecie: PrzypiecieSchema,
  /** `null`, gdy pytamy bez wyspy. W przeciwnym razie: czy ta paczka czeka właśnie na nią. */
  dlaTejWyspy: z.boolean().nullable(),
  /** Gotowe zdanie, dlaczego nie da się jej wprowadzić do magazynu wyspy; `null` = da się. */
  powodOdmowy: z.string().nullable(),
});

export type PaczkaPoczekalni = z.infer<typeof PaczkaPoczekalniSchema>;

const PolkaSchema = z.array(PaczkaPoczekalniSchema);

export const fetchPoczekalnia = (groupId?: string) =>
  apiGet(
    groupId === undefined ? '/kopie-poczekalnia' : `/kopie-poczekalnia?groupId=${encodeURIComponent(groupId)}`,
    PolkaSchema,
  );

export const przejmijPaczke = (id: string, groupId: string, agentId: string) =>
  apiPost(`/kopie-poczekalnia/${id}/przejmij`, { groupId, agentId }, z.object({ id: z.string(), nazwa: z.string() }));

export const usunZPoczekalni = (id: string) =>
  apiSend('DELETE', `/kopie-poczekalnia/${id}`, undefined, z.object({ usunieto: z.literal(true) }));

const WgranaSchema = z.object({
  id: z.string(),
  nazwa: z.string(),
  rozmiar: z.number(),
  suma: z.string(),
});

/** Do czego przypinamy paczkę. `POLKA` = do niczego, leży na wspólnej półce. */
export type Cel = { rodzaj: 'WYSPA'; groupId: string } | { rodzaj: 'KOD'; kodId: string } | { rodzaj: 'POLKA' };

/**
 * IDENTYFIKATOR PRZYPIĘCIA DO PARAMETRU — albo wyjątek, zanim cokolwiek pojedzie.
 *
 * ⚠️⚠️ POWSTAŁO Z BŁĘDU ZGŁOSZONEGO 09.09.2026: „nie może wstawić pliku bak do proja bez
 * agentów", a z API wracało `groupId: Nieprawidłowy UUID`. Sedno: `URLSearchParams.set` przy
 * `null` (i `undefined`) **nie rzuca** — wpisuje to jako NAPIS `"null"` / `"undefined"`,
 * który jedzie jako prawidłowa treść parametru i wywraca się dopiero na walidacji po drugiej stronie. Pomyłkę
 * w kodzie widać wtedy jako komunikat o UUID-zie, czyli w miejscu, które nie ma z nią nic
 * wspólnego — i tak właśnie zostało to odczytane.
 *
 * ⚠️ REGUŁĘ „co przypinamy" trzyma `lib/cel-wgrania.ts`. To jest tylko siatka pod nią: gdyby
 * kiedyś ktoś zbudował `Cel` ręcznie, wysyłka staje tutaj, a nie u klienta na ekranie.
 */
function idPrzypiecia(id: string | null | undefined, pole: 'groupId' | 'kodId'): string {
  const czysty = (id ?? '').trim();
  if (czysty === '') throw new Error(`Puste przypięcie (${pole}) — paczka nie ma do czego się przypiąć`);
  return czysty;
}

/**
 * WGRANIE PACZKI NA PÓŁKĘ.
 *
 * ⚠️ `XMLHttpRequest`, A NIE `fetch` — z tego samego powodu co przy wgrywaniu na wyspę: `fetch`
 * nie raportuje postępu WYSYŁANIA, a to kilka minut ekranu, na którym nic się nie dzieje.
 *
 * ⚠️ CIAŁEM JEST SAM PLIK. `Content-Type` musi być jednym z dwóch, które zna parser surowego
 * ciała w API — inaczej Fastify odrzuca żądanie `415` po przesłaniu całej paczki.
 */
export function wgrajDoPoczekalni(
  cel: Cel,
  plik: File,
  postep?: (procent: number) => void,
): Promise<z.infer<typeof WgranaSchema>> {
  return new Promise((rozwiaz, odrzuc) => {
    const q = new URLSearchParams({ nazwa: plik.name });
    if (cel.rodzaj === 'WYSPA') q.set('groupId', idPrzypiecia(cel.groupId, 'groupId'));
    if (cel.rodzaj === 'KOD') q.set('kodId', idPrzypiecia(cel.kodId, 'kodId'));

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API_BASE}/kopie-poczekalnia?${q.toString()}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader(
      'Content-Type',
      /\.zip$/i.test(plik.name) ? 'application/zip' : 'application/octet-stream',
    );

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && postep !== undefined) postep(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      const tresc: unknown = ((): unknown => {
        try {
          return JSON.parse(xhr.responseText) as unknown;
        } catch {
          return null;
        }
      })();
      if (xhr.status >= 200 && xhr.status < 300) {
        const wynik = WgranaSchema.safeParse(tresc);
        wynik.success ? rozwiaz(wynik.data) : odrzuc(new Error('Instancja przyjęła paczkę, ale odpowiedź jest nie ta'));
        return;
      }
      const wiadomosc =
        tresc !== null && typeof tresc === 'object' && 'message' in tresc
          ? String((tresc as { message: unknown }).message)
          : `Wysyłka odrzucona (HTTP ${xhr.status})`;
      odrzuc(new Error(wiadomosc));
    };
    /** ⚠️ Sieć urwana w połowie nie daje ŻADNEJ treści — bez tego zdania zostałby pusty błąd. */
    xhr.onerror = () => odrzuc(new Error('Połączenie przerwane w trakcie wysyłki — paczka nie doszła w całości'));
    xhr.onabort = () => odrzuc(new Error('Wysyłka przerwana'));

    xhr.send(plik);
  });
}
